import "./TicketingOverview.scss";

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFirestoreErrorTranslation } from "../../../../utility/useFirestoreErrorTranslation";
import ModalComponent from "../../../../components/modal/Modal";
import EventCard from "../../../../components/ticketing/eventCard/EventCard";
import EmptyState from "../../../../components/emptyState/EmptyState";
import { Loader2 } from "../../../../helper/loader";
import { ReactComponent as Plus } from "../../../../images/Plus.svg";
import { getCollection } from "../../../../utility/getCollection";
import getStripeAccount from "../../../../utility/getStripeAccount";
import createStripeAccountLink from "../../../../utility/createStripeAccountLink";
import MerchantInscription from "../merchantInscription/MerchantInscription";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function TicketingOverview({ org }) {
  const { t } = useTranslation("ticketingOverview");

  const NB_EVENT_REQUEST = 8;
  const [events, setEvents] = useState([]);
  const eventLastDoc = useRef(null);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageCursors = useRef([null]);
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modalInformations, setModalInformations] = useState({
    content: "",
    title: "",
  });
  const errorTranslation = useFirestoreErrorTranslation();
  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    setStripeStatus(null);
    const getStripeData = async () => {
      try {
        const account = await getStripeAccount(org?.stripeId);
        const requirements = account?.requirements || {};
        const futureRequirements = account?.future_requirements || {};

        const currentlyDue = Array.isArray(requirements.currently_due)
          ? requirements.currently_due.length
          : 0;
        const eventuallyDue = Array.isArray(requirements.eventually_due)
          ? requirements.eventually_due.length
          : 0;
        const futureCurrentlyDue = Array.isArray(futureRequirements.currently_due)
          ? futureRequirements.currently_due.length
          : 0;
        const futureEventuallyDue = Array.isArray(futureRequirements.eventually_due)
          ? futureRequirements.eventually_due.length
          : 0;

        const reqDisabledReason = requirements?.disabled_reason || "";
        const futureDisabledReason = futureRequirements?.disabled_reason || "";
        const backendStatusRaw =
          account?.status || account?.restriction_status || account?.account_status || "";
        const backendStatus = String(backendStatusRaw).toLowerCase();

        const hasDeadline = Boolean(
          requirements?.current_deadline || futureRequirements?.current_deadline,
        );

        const hasKnownRestrictedSoonLabel =
          backendStatus.includes("restricted_soon") ||
          backendStatus.includes("restriction_soon") ||
          backendStatus.includes("restricted soon") ||
          backendStatus.includes("prochainement limité") ||
          backendStatus.includes("prochainement limite");

        const isPastDue =
          reqDisabledReason === "requirements.past_due" ||
          futureDisabledReason === "requirements.past_due";

        const hasCurrentlyDueRequirements = currentlyDue > 0 || futureCurrentlyDue > 0;
        const hasRequiredActionStatus =
          backendStatus.includes("paused") ||
          backendStatus.includes("action_required") ||
          backendStatus.includes("action required") ||
          (backendStatus.includes("restricted") && !hasKnownRestrictedSoonLabel);

        const requiresAction =
          isPastDue || hasCurrentlyDueRequirements || hasRequiredActionStatus;

        const isSoonRestrictedByRequirements =
          !requiresAction &&
          hasDeadline &&
          (eventuallyDue > 0 || futureEventuallyDue > 0);

        const isSoonRestricted = hasKnownRestrictedSoonLabel || isSoonRestrictedByRequirements;

        setStripeStatus({
          requiresAction,
          isSoonRestricted,
          currentDeadline: requirements?.current_deadline || futureRequirements?.current_deadline || null,
        });
      } catch (error) {
        setStripeStatus({ requiresAction: false, isSoonRestricted: false, currentDeadline: null });
        setModalInformations({
          title: t("errorFetch"),
          content: errorTranslation(error),
        });
      }
    };

    if (org?.stripeActivated && org?.stripeId) {
      getStripeData();
    }
  }, [org]);

  function getActiveFilters() {
    const filters = [];
    if (startDateFilter) filters.push({ field: "endDate", operator: ">=", value: new Date(startDateFilter).getTime() });
    if (endDateFilter) {
      const date = new Date(endDateFilter);
      date.setHours(23, 59, 59, 999);
      filters.push({ field: "endDate", operator: "<=", value: date.getTime() });
    }
    return filters;
  }

  async function fetchEvents({ nextPage = 1, cursor = null } = {}) {
    setLoading(true);
    if (org) {
      try {
        const hasDateFilter = Boolean(startDateFilter || endDateFilter);
        // Firestore requires the inequality field to be the first sort field.
        const effectiveSort = hasDateFilter ? "endDate" : sortBy;
        const eventsData = await getCollection({
          collectionName: "Events",
          lastDocument: cursor,
          pageSize: NB_EVENT_REQUEST,
          filterField: "orgId",
          filterValue: org.id,
          filters: getActiveFilters(),
          orderByField: effectiveSort,
          orderDirection: "desc",
        });
        setEvents(eventsData.data);
        eventLastDoc.current = eventsData.lastDoc;
        pageCursors.current[nextPage] = eventsData.lastDoc;
        setHasNextPage(eventsData.data.length === NB_EVENT_REQUEST);
        setPage(nextPage);
      } catch (error) {
        setModalInformations({
          title: t("errorFetch"),
          content: errorTranslation(error),
        });
        console.error(t("errorFetch"), error);
      }
    }
    setLoading(false);
  }

  function resetAndFetchEvents() {
    eventLastDoc.current = null;
    pageCursors.current = [null];
    fetchEvents({ nextPage: 1, cursor: null });
  }

  useEffect(() => {
    if (org?.stripeActivated && org?.stripeId) resetAndFetchEvents();
  }, [org, startDateFilter, endDateFilter, sortBy]);

  function goToNextPage() {
    if (!hasNextPage || loading) return;
    fetchEvents({ nextPage: page + 1, cursor: eventLastDoc.current });
  }

  function goToPreviousPage() {
    if (page <= 1 || loading) return;
    const previousPage = page - 1;
    fetchEvents({ nextPage: previousPage, cursor: pageCursors.current[previousPage - 1] || null });
  }

  function goToDashboard(event) {
    navigate("/dashboard/ticketing-dashboard", { state: { eventId: event.id, orgId: event.orgId || org.id } });
  }

  function goToEdit(event) {
    navigate("/dashboard/ticketing-create-edit", { state: { event } });
  }

  function goToCreate() {
    navigate("/dashboard/ticketing-create-edit", { state: { orgId: org.id } });
  }

  async function createStripeLink() {
    try {
      const link = await createStripeAccountLink(org?.stripeId, {
        requirementScope: stripeStatus?.requiresAction ? "currently_due" : "eventually_due",
        reason: stripeStatus?.requiresAction ? "requirements_due" : "restricted_soon",
        linkType: "account_update",
      });
      window.open(link.url, "_blank");
    } catch (error) {
      setModalInformations({
        title: t("errorFetch"),
        content: errorTranslation(error),
      });
    }
  }

  const requiresStripeAction = stripeStatus?.requiresAction || stripeStatus?.isSoonRestricted;
  const shouldShowCreateEventButton = stripeStatus && !requiresStripeAction;

  return !org?.stripeActivated ? (
    <MerchantInscription org={org} />
  ) : (
    <div className="ticketingOverviewMainContainer">
      {loading && <Loader2 />}
      <ModalComponent
        isOpen={!!modalInformations.title}
        onClose={() => setModalInformations({ title: "", content: "" })}
        title={modalInformations.title}
        message={modalInformations.content}
      />
      <div className="titleContainer">{t("ticketingTitle")}</div>
      <div className="yourSalesContainer">
        <p className="yourSales">{t("yourSales")}</p>
        <button onClick={goToCreate} disabled={!(stripeStatus && shouldShowCreateEventButton)}>
          <Plus />
          <p>{t("createNewSale")}</p>
        </button>
      </div>
      {requiresStripeAction && (
        <div className="stripeLimitedSoonNotice">
          <p>
            {stripeStatus?.requiresAction
              ? t("stripe_action_required_warning")
              : t("stripe_limited_soon_warning")}
          </p>
          <button onClick={createStripeLink}>{t("open_stripe_requirements")}</button>
        </div>
      )}

      <div className="ticketingFilters" aria-label={t("filters")}>
        <label>
          {t("salesEndFrom")}
          <DatePicker
            selected={startDateFilter}
            onChange={setStartDateFilter}
            dateFormat="dd/MM/yyyy"
            placeholderText={t("selectDate")}
            isClearable
            className="ticketingFilterDatePicker"
          />
        </label>
        <label>
          {t("salesEndTo")}
          <DatePicker
            selected={endDateFilter}
            onChange={setEndDateFilter}
            dateFormat="dd/MM/yyyy"
            placeholderText={t("selectDate")}
            isClearable
            className="ticketingFilterDatePicker"
          />
        </label>
        <label>
          {t("sortBy")}
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="createdAt">{t("sortCreatedAt")}</option>
            <option value="endDate">{t("sortEndDate")}</option>
          </select>
        </label>
      </div>

      {events?.length === 0 && !loading ? (
        <div className="salesEmptyContainer">
          <EmptyState
            title={t("emptyStateTitle")}
            text={t("emptyStateText")}
            buttonLabel={
              stripeStatus && !shouldShowCreateEventButton
                ? t("open_stripe_requirements")
                : shouldShowCreateEventButton
                  ? t("createNewSale")
                  : undefined
            }
            onAction={
              stripeStatus && !shouldShowCreateEventButton
                ? createStripeLink
                : shouldShowCreateEventButton
                  ? goToCreate
                  : undefined
            }
          />
        </div>
      ) : (
        <>
          <div className="salesContainer">
            {events.map((event) => (
              <EventCard
                event={event}
                onShowClicked={goToDashboard}
                onEditClicked={goToEdit}
                key={event.id}
              />
            ))}
          </div>
          <div className="ticketingPagination">
            <button type="button" onClick={goToPreviousPage} disabled={page === 1 || loading}>
              {t("previousPage")}
            </button>
            <span>{t("page", { page })}</span>
            <button type="button" onClick={goToNextPage} disabled={!hasNextPage || loading}>
              {t("nextPage")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default TicketingOverview;
